// WEBAPP-021: Caricamento paginato con infinite scroll - Lazy loading
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Plus, RefreshCw, Info } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../../../contexts/AuthContext';
import ExerciseList from '../../ExerciseList';
import ExerciseForm from '../../exercises/ExerciseForm';

const GymExercisesManager = () => {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();
    const { t } = useTranslation();
    
    const [showModal, setShowModal] = useState(false);
    const [editingExercise, setEditingExercise] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0); // Key per forzare refresh di ExerciseList

    const handleCreateExercise = () => {
        setEditingExercise(null);
        setShowModal(true);
    };

    const handleEditExercise = (exercise) => {
        setEditingExercise(exercise);
        setShowModal(true);
    };

    const handleDeleteExercise = async (exerciseId) => {
        const confirmed = window.confirm(
            t('gym.exercises.deleteConfirm', { name: 'esercizio' }).trim()
        );

        if (!confirmed) {
            return;
        }

        try {
            await api.delete(`/esercizi.php?id=${exerciseId}`);
            toast.success(t('gym.exercises.deleteSuccess'));
            // Forza refresh di ExerciseList
            setRefreshKey(prev => prev + 1);
        } catch (err) {
            console.error('Errore nell\'eliminazione dell\'esercizio:', err);
            const message = err.response?.data?.message || t('gym.exercises.deleteError');
            toast.error(message);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingExercise(null);
    };

    const handleExerciseSaved = async () => {
        const wasEditing = Boolean(editingExercise);
        // Forza refresh di ExerciseList
        setRefreshKey(prev => prev + 1);
        closeModal();
        toast.success(wasEditing ? t('gym.exercises.updateSuccess') : t('gym.exercises.createSuccess'));
    };

    const handleRefresh = () => {
        // Forza refresh di ExerciseList
        setRefreshKey(prev => prev + 1);
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <div className="flex-1 flex flex-col min-h-0 w-full">
                {/* Header fisso */}
                <div className="flex-shrink-0 px-6 pt-8 pb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <Dumbbell className="h-8 w-8 text-indigo-500 dark:text-gray-300" />
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                {t('gym.exercises.title')}
                            </h1>
                            <div className="relative group">
                                <Info className="h-5 w-5 cursor-help text-gray-400 hover:text-gray-600 dark:hover:text-white/80" />
                                <div className="invisible absolute left-0 top-full z-10 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100 dark:border-white/10 dark:bg-gray-900 dark:text-white/85">
                                    {t('gym.exercises.imageManagedExternally')}
                                </div>
                            </div>
                        </div>
                        <p className="mt-2 max-w-2xl text-gray-600 dark:text-white/75">
                            {t('gym.exercises.subtitle')}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleRefresh}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/10 dark:text-white/90 dark:hover:bg-gray-700"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <span>{t('common.refresh')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleCreateExercise}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                        >
                            <Plus className="h-4 w-4" />
                            <span>{t('gym.exercises.addExercise')}</span>
                        </button>
                        </div>
                    </div>
                </div>

                {/* Container scrollabile per la lista esercizi */}
                <div className="flex-1 min-h-0 overflow-hidden px-6 pb-8">
                    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white shadow dark:border-white/10 dark:bg-gray-900">
                        <div className="flex-1 min-h-0 overflow-y-auto p-4">
                            <ExerciseList
                                key={refreshKey} // Key per forzare refresh
                                esercizi={[]}
                                onEdit={null} // WEBAPP-006: Disabilitata modifica dall'archivio palestra
                                onDelete={null} // WEBAPP-006: Disabilitata eliminazione dall'archivio palestra
                                useInternalLoading={true}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
                        <ExerciseForm
                            exercise={editingExercise}
                            onExerciseAdded={handleExerciseSaved}
                            onClose={closeModal}
                            allowImageEdit={false}
                            imageInfoMessage={t('gym.exercises.imageManagedExternally')}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GymExercisesManager;
